import { useEffect, useState } from 'react';
import { api } from './lib/api';
import { apiV2 } from './lib/apiV2';
import type { HostEvent } from '../shared/protocol';
import { getSession, onSessionChange, signOut, verifyLoginToken, type Session } from './lib/auth';
import { loadDraft, saveDraft, emptyDraft } from './lib/draft';
import { AuthSheet } from './components/AuthSheet';
import { TermsGate } from './components/TermsGate';
import { fetchTermsStatus } from './lib/terms';
import type { TermsStatus } from '../shared/protocol';
import { CreateEvent } from './components/CreateEvent';
import { HostDashboard } from './components/HostDashboard';
import { VotePage } from './components/VotePage';
import { FriendsManager } from './components/FriendsManager';
import { Settings } from './components/Settings';
import { FunnelDashboard } from './components/FunnelDashboard';
import { LinkDevice } from './components/LinkDevice';
import { MyNights } from './components/MyNights';
import { AvatarMaker } from './components/AvatarMaker';
import { GuidePage, GuidesRow, type GuideId } from './components/Guides';

type View =
  | { kind: 'home'; draftElsewhere?: boolean }
  | { kind: 'create'; step?: 1 | 2 | 3 }
  | { kind: 'vote'; eventId: string; voterCode?: string }
  | { kind: 'guide'; guide: GuideId; room?: string }
  | { kind: 'friends' }
  | { kind: 'settings' }
  | { kind: 'funnel' }
  | { kind: 'link' }
  | { kind: 'avatar' }
  | { kind: 'error'; message: string; offerSignIn?: boolean };

function isGuide(g: string | null): g is GuideId {
  return g === 'host' || g === 'vr' || g === 'browser';
}

/** DEV-only demo seeding for the screenshot rig (?seed=1). Stripped from prod builds. */
function maybeSeedDemo(p: URLSearchParams): void {
  if (!import.meta.env.DEV || !p.get('seed')) return;
  try {
    localStorage.setItem(
      'bfn_session_v1',
      JSON.stringify({
        access_token: 'dev-token',
        refresh_token: 'dev-refresh',
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        email: 'designfaize@gmail.com',
      }),
    );
    const d = emptyDraft();
    d.options = [
      {
        title: 'The Iron Giant',
        year: 1999,
        posterUrl: `${window.location.origin}/api/poster/1.svg`,
        tmdbId: 10386,
      },
    ];
    saveDraft(d);
  } catch {
    // storage blocked (e.g. third-party iframe) — the app still renders
  }
}

/**
 * App shell + tiny query-param router.
 *   ?e=<id>&host=<token>  → host dashboard      ?e=<id>[&c=<code>] → vote page
 *   ?i=<code>             → short invite link   ?g=host|vr|browser  → guides
 *   ?tab=friends|settings|how                   ?plan=1[&step=N]    → create stepper
 *   ?auth=<token_hash>    → magic-link exchange ?draft=1            → restore draft (screen 3)
 */
export function App() {
  const [params] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    maybeSeedDemo(p);
    return p;
  });
  const [session, setSession] = useState<Session | null>(getSession);
  const [view, setView] = useState<View | null>(null); // null = booting
  const [bootMsg, setBootMsg] = useState('Loading…');
  const [hostEvent, setHostEvent] = useState<HostEvent | null>(null);
  const [hostToken, setHostToken] = useState<string | null>(null);
  // standalone sign-in sheet (topbar / hero links) — the same magic-link
  // AuthSheet the create flow uses, opened WITHOUT starting the create flow
  const [signInOpen, setSignInOpen] = useState(false);
  // THU-115: null until asked, and null again whenever nobody is signed in.
  const [terms, setTerms] = useState<TermsStatus | null>(null);

  useEffect(() => onSessionChange(setSession), []);

  // Ask on every session change, not once on mount: signing in IS the moment
  // a first-time user acquires an account that can owe an agreement.
  useEffect(() => {
    if (!session) {
      setTerms(null);
      return;
    }
    let alive = true;
    void fetchTermsStatus().then((s) => {
      if (alive) setTerms(s);
    });
    return () => {
      alive = false;
    };
  }, [session]);

  // ---- boot: auth exchange → invite resolve → route ----
  useEffect(() => {
    let alive = true;
    (async () => {
      const p = new URLSearchParams(params);

      // 1) magic-link return: exchange ?auth=<token_hash> for a session
      const authHash = p.get('auth');
      if (authHash) {
        setBootMsg('Signing you in…');
        try {
          await verifyLoginToken(authHash);
        } catch (e) {
          if (!alive) return;
          // A dead magic link told the reader to request a fresh one and then
          // offered only "Back to home" (THU-185). The one action the message
          // names has to be ON the screen that names it.
          setView({
            kind: 'error',
            message: e instanceof Error ? e.message : 'Sign-in failed.',
            offerSignIn: true,
          });
          return;
        }
        p.delete('auth');
        p.delete('seed');
        // a sign-in started inside a flow that asked to come back (the avatar maker)
        try {
          const back = localStorage.getItem('bfn_after_auth');
          if (back) {
            localStorage.removeItem('bfn_after_auth');
            new URLSearchParams(back).forEach((v, k) => p.set(k, v));
          }
        } catch { /* storage blocked: home page */ }
        const qs = p.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }

      // 2) short invite link: /i/<code> → ?i=<code> → resolve → vote page
      const inviteCode = p.get('i');
      if (inviteCode) {
        setBootMsg('Finding your invite…');
        try {
          const r = await apiV2.resolveInvite(inviteCode);
          if (!alive) return;
          const u = new URL(window.location.href);
          u.search = '';
          u.searchParams.set('e', r.eventId);
          u.searchParams.set('c', r.voterCode);
          window.history.replaceState({}, '', u.toString());
          setView({ kind: 'vote', eventId: r.eventId, voterCode: r.voterCode });
        } catch {
          if (!alive) return;
          setView({
            kind: 'error',
            // Invite links do not expire — there is no TTL anywhere in the
            // invite path. A failure here means the code did not resolve, so
            // don't tell the guest it timed out.
            message: "We couldn't find that invite link — ask the host to resend it.",
          });
        }
        return;
      }

      // 3) plain routes
      const eventId = p.get('e') ?? undefined;
      const voterCode = p.get('c') ?? undefined;
      const token = p.get('host') ?? undefined;
      const g = p.get('g');
      const tab = p.get('tab');

      if (eventId && token) {
        setBootMsg('Loading your dashboard…');
        try {
          const ev = await api.getHostEvent(eventId, token);
          if (!alive) return;
          setHostEvent(ev);
          setHostToken(token);
          setView({ kind: 'home' }); // dashboard renders whenever hostEvent is set
        } catch {
          if (!alive) return;
          setView({ kind: 'error', message: 'That host link is invalid or expired.' });
        }
        return;
      }
      if (eventId) {
        setView({ kind: 'vote', eventId, voterCode });
        return;
      }
      if (isGuide(g)) {
        setView({ kind: 'guide', guide: g, room: p.get('room') ?? undefined });
        return;
      }
      if (tab === 'friends') {
        setView({ kind: 'friends' });
        return;
      }
      if (tab === 'avatar') {
        setView({ kind: 'avatar' });
        return;
      }
      if (tab === 'settings') {
        setView({ kind: 'settings' });
        return;
      }
      // THU-242: the owner's event-funnel dashboard (the worker enforces admin-only).
      if (p.has('funnel') || tab === 'funnel') {
        setView({ kind: 'funnel' });
        return;
      }
      // Headset pairing: the game tells the player to open /schedule/?link
      if (p.has('link') || tab === 'link') {
        setView({ kind: 'link' });
        return;
      }

      // ?draft=1 (magic-link round trip): restore straight to screen 3
      if (p.get('draft')) {
        if (loadDraft()) {
          setView({ kind: 'create', step: 3 });
        } else {
          setView({ kind: 'home', draftElsewhere: true });
        }
        return;
      }
      if (p.get('plan') || tab === 'plan') {
        const step = Number(p.get('step'));
        setView({ kind: 'create', step: step === 2 || step === 3 ? (step as 2 | 3) : 1 });
        return;
      }
      setView({ kind: 'home' });
    })();
    return () => {
      alive = false;
    };
  }, [params]);

  const goHome = () => {
    window.history.pushState({}, '', window.location.pathname);
    window.location.reload();
  };

  /**
   * Enter the create flow AND say so in the address bar.
   *
   * THU-239: this used to be a bare setView, so the URL kept whatever it had
   * while the wizard rendered — the bug report was filed off a screenshot
   * showing step "1 of 3" at `?tab=settings`, and that mismatch is why the
   * page looked like it had ignored the tab. `?plan=1` is a route the boot
   * router already understands, so a reload or a back-button lands where the
   * screen says it is.
   */
  const goCreate = () => {
    window.history.pushState({}, '', `${window.location.pathname}?plan=1`);
    setView({ kind: 'create' });
  };

  /** Leave the create flow, dropping ?plan=1 so a reload doesn't re-enter it. */
  const exitCreate = () => {
    window.history.pushState({}, '', window.location.pathname);
    setView({ kind: 'home' });
  };

  const inCreate = view?.kind === 'create';
  const showDashboard = hostEvent !== null && hostToken !== null;
  const isVoteLink = view?.kind === 'vote';

  const header = (
    <header className="topbar">
      <div className="brand" onClick={goHome} title="Thursday Night home">
        <img className="brand-logo" src="/assets/logo.webp" alt="Thursday Night" />
      </div>
      {!isVoteLink && (
        <nav className="nav">
          {!inCreate && !showDashboard && (
            <button className="nav-link" onClick={goCreate}>
              Plan a night
            </button>
          )}
          <a className="nav-link" href="?g=browser">
            Guides
          </a>
          {!session && (
            <button className="btn secondary small" onClick={() => setSignInOpen(true)}>
              Sign in
            </button>
          )}
          {session && (
            <>
              <a className="nav-link" href="?tab=friends">
                Friends
              </a>
              <a className="nav-link" href="?tab=settings">
                Settings
              </a>
            </>
          )}
        </nav>
      )}
      {session ? (
        <div className="session-chip" title={session.email}>
          <span className="session-email">{session.email}</span>
          <button
            className="btn link small"
            onClick={() => {
              signOut();
              goHome();
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </header>
  );

  let body: React.ReactNode;

  if (view === null) {
    body = (
      <div className="card center">
        <span className="spinner" /> {bootMsg}
      </div>
    );
  } else if (showDashboard && hostEvent && hostToken) {
    body = <HostDashboard initial={hostEvent} token={hostToken} />;
  } else if (view.kind === 'error') {
    body = (
      <div className="card">
        <div className="notice error">{view.message}</div>
        {view.offerSignIn && (
          <button className="btn primary block" onClick={() => setSignInOpen(true)}>
            Email me a new sign-in link
          </button>
        )}
        <button className="btn secondary" onClick={goHome}>
          Back to home
        </button>
      </div>
    );
  } else if (view.kind === 'vote') {
    body = <VotePage eventId={view.eventId} voterCode={view.voterCode} />;
  } else if (view.kind === 'guide') {
    body = <GuidePage guide={view.guide} roomCode={view.room} />;
  } else if (view.kind === 'friends') {
    body = <FriendsManager />;
  } else if (view.kind === 'settings') {
    body = <Settings />;
  } else if (view.kind === 'funnel') {
    body = <FunnelDashboard />;
  } else if (view.kind === 'link') {
    body = <LinkDevice />;
  } else if (view.kind === 'avatar') {
    body = <AvatarMaker />;
  } else if (view.kind === 'create') {
    body = (
      <CreateEvent
        initialStep={view.step}
        onExit={exitCreate}
        onCreated={(ev) => {
          setHostEvent(ev);
          setHostToken(ev.hostToken);
        }}
      />
    );
  } else {
    body = (
      <>
        <div className="hero-backdrop" aria-hidden="true" />
        {view.draftElsewhere && (
          <div className="notice info">
            You’re signed in. Your draft is on the device where you started — or plan a fresh night
            in a minute.
          </div>
        )}
        {/* THU-239: a signed-in host's own nights, ABOVE the hero. The hero
            invites you to plan a NEW night, which is the wrong first thing to
            show someone who already has one and came back to check on it. */}
        {session && <MyNights onPlan={goCreate} />}
        <div className="hero card marquee bulbs">
          <h1 className="display">
            Thursday <span className="gold">Night</span>
          </h1>
          <p>Movie night with your crew — one minute to plan, everyone reminded.</p>
          <button className="btn primary cta" onClick={goCreate}>
            Plan a night
          </button>
          <p className="hint-line">Nothing scheduled? It takes about a minute.</p>
          {!session && (
            <p className="hint-line">
              Been here before?{' '}
              <button className="btn link small hero-signin" onClick={() => setSignInOpen(true)}>
                Sign in
              </button>
            </p>
          )}
        </div>
        <h3 className="section-heading display">How it works</h3>
        <GuidesRow />
      </>
    );
  }

  const wide = showDashboard || isVoteLink;

  return (
    <div className={`wrap${wide ? ' wide' : ''}`}>
      {header}
      {body}
      {/* Not gated on `!session`: the dead-magic-link screen's own button sets
          this, and a signed-in person who clicks a stale link would otherwise
          press it and watch nothing happen — the same "names an action it does
          not offer" defect one layer down. Asking for a fresh link while signed
          in is harmless; a button that does nothing is not. */}
      {signInOpen && (
        <AuthSheet overlay title="Sign in" onClose={() => setSignInOpen(false)} />
      )}
      {/* THU-115: a signed-in person who has not agreed to the CURRENT terms
          gets this over everything else. `required` is the server's answer, and
          it is false whenever the record could not be read at all — see
          worker/src/terms.ts for why that direction is the safe one. */}
      {session && terms?.required && (
        <TermsGate
          status={terms}
          onAccepted={() => setTerms({ ...terms, required: false })}
        />
      )}
      <footer className="footer">
        <div>Thursday Night · plan the movie, then hop into the VR theater · $0, no tracking</div>
        <div className="foot-guides">
          <a href="?g=host">Host a Night</a> · <a href="?g=vr">Join in VR</a> ·{' '}
          <a href="?g=browser">Watch in Your Browser</a>
        </div>
        {/* Legal pages live at the ROOT of thursday-night.com, served by the
            front-door worker — this SPA is proxied under /schedule, so a
            root-relative "/terms" would be right in prod and wrong everywhere
            else (pages.dev preview, localhost). Absolute, same as the share
            links, for the same reason. */}
        <div className="foot-legal">
          <a href="https://thursday-night.com/terms">Terms of Service</a> ·{' '}
          <a href="https://thursday-night.com/privacy">Privacy Policy</a>
        </div>
        {/* Attribution required by the providers' terms of use: TMDB asks for this
            disclaimer, and fanart.tv requires we "inform users of this website and
            the images you use." Non-commercial / private use only — fanart.tv needs
            written consent for commercial use. */}
        <div className="credits">
          Posters &amp; fan art via{' '}
          <a href="https://www.themoviedb.org/" target="_blank" rel="noreferrer noopener">
            TMDB
          </a>{' '}
          and{' '}
          <a href="https://fanart.tv/" target="_blank" rel="noreferrer noopener">
            fanart.tv
          </a>
          . This product uses the TMDB and fanart.tv APIs but is not endorsed or certified by them.
        </div>
      </footer>
    </div>
  );
}
